import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SivaGoalAccomplishmentService, TargetNumberDto, TargetNumberService } from 'src/api/v2';
import {
  SIVA_CLUSTER_DETAILS_KEY,
  SIVA_COMPANY_TARGET_NUMBERS,
  SIVA_GOALS_TARGET_NUMBERS_KEY,
  SIVA_TOTAL_GOAL_ACCOMPLISHMENT_KEY,
} from 'src/constants/hook-keys';
import { toast } from 'react-toastify';
import ToastContent from 'src/components/ToastContent/ToastContent';

const useTargetNumbers = (goalAccomplishmentId: string, goalTemplateId: string) => {
  const [goalService] = useState(() => new SivaGoalAccomplishmentService());
  const [targetService] = useState(() => new TargetNumberService());
  const queryClient = useQueryClient();

  const { data, dataUpdatedAt } = useQuery({
    queryFn: () =>
      goalAccomplishmentId
        ? goalService.getGoalAccomplishmentWithTargetNumbers(goalAccomplishmentId)
        : undefined,
    queryKey: [SIVA_GOALS_TARGET_NUMBERS_KEY, goalTemplateId, goalAccomplishmentId],
    onSuccess: () => {
      queryClient.resetQueries([SIVA_CLUSTER_DETAILS_KEY]);
      queryClient.resetQueries([SIVA_TOTAL_GOAL_ACCOMPLISHMENT_KEY]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: TargetNumberDto[]) => {
      if (!values || values.length < 1) {
        return Promise.reject('No values have been selected');
      }
      return targetService.update(values).catch((err) => {
        if (typeof err === 'object' && err !== null && 'Detail' in err && typeof err.Detail === 'string') {
          toast(
            <ToastContent
              success={false}
              text={err && err.Detail}
              title={'Noe hindret oss i å oppdatere måloppnåelse'}
            />,
          );
        }
      });
    },
    onSuccess: () => {
      queryClient.resetQueries([SIVA_CLUSTER_DETAILS_KEY]);
      queryClient.refetchQueries([SIVA_GOALS_TARGET_NUMBERS_KEY, goalTemplateId]);
      queryClient.refetchQueries([SIVA_COMPANY_TARGET_NUMBERS]);
    },
  });

  return {
    extendedGoal: data,
    updateMutation,
    updatedAt: dataUpdatedAt,
  };
};

export default useTargetNumbers;
